import { css, theme } from '@kui-shared/styles';

const RadioStyle = css({
  // Reset
  appearance: 'none',
  // Layout
  border: `${theme.borderWidths.md} solid ${theme.colors.actionBorderDefault}`,
  borderRadius: '50%',
  display: 'grid',
  height: theme.space.md,
  placeContent: 'center',
  width: theme.space.md,
  // Color
  backgroundColor: theme.colors.actionBgDefault,
  // Unchecked States
  '&:hover:enabled': {
    backgroundColor: theme.colors.actionBgHover,
    borderColor: theme.colors.actionBorderHover
  },
  '&:active:enabled': {
    backgroundColor: theme.colors.actionBgActive,
    borderColor: theme.colors.actionBorderActive
  },
  // Checked States
  '&::before': {
    borderRadius: '50%',
    boxShadow: `inset 1em 1em ${theme.colors.actionFgSelected}`,
    content: '',
    height: theme.space.sm,
    transform: 'scale(0)',
    width: theme.space.sm,
    /**
     * Windows High Contrast Mode
     * {@link: https://docs.microsoft.com/en-us/windows/apps/design/accessibility/high-contrast)}
     * {@link: https://developer.mozilla.org/en-US/docs/Web/CSS/@media/forced-colors}
     *
     * Because we are using a box-shadow to create the checkmark, we need to
     * account for users in WHCM, for whom the shadow would be removed. We
     * ensure that there is a background and thus, they would be able to see
     * it. Since the shadow sits on top of the background, normal users won't
     * see the background.
     */
    backgroundColor: 'CanvasText'
  },
  '&:checked': {
    backgroundColor: theme.colors.actionBgSelected,
    borderColor: theme.colors.actionBorderSelected
  },
  '&:checked::before': {
    transform: 'scale(1)',
    transition: '120ms transform ease-in-out'
  },
  '&:checked:hover:enabled': {
    backgroundColor: theme.colors.actionBgSelectedHover,
    borderColor: theme.colors.actionBorderSelectedHover
  },
  '&:checked:hover:enabled::before': {
    boxShadow: `inset 1em 1em ${theme.colors.actionFgSelectedHover}`
  },
  '&:checked:active:enabled': {
    backgroundColor: theme.colors.actionBgSelectedActive,
    borderColor: theme.colors.actionBorderSelectedActive
  },
  '&:checked:active:enabled::before': {
    boxShadow: `inset 1em 1em ${theme.colors.actionFgSelectedActive}`
  },
  // Disabled State
  '&:disabled': {
    backgroundColor: theme.colors.actionBgDisabled,
    borderColor: theme.colors.actionBorderDisabled
  },
  '&:disabled:checked': {
    backgroundColor: theme.colors.actionBgSelectedDisabled,
    borderColor: theme.colors.actionBorderDisabled
  },
  '&:disabled:checked::before': {
    boxShadow: `inset 1em 1em ${theme.colors.actionFgDisabled}`
  },
  '&:disabled:hover': {
    cursor: 'not-allowed'
  },
  variants: {
    status: {
      invalid: {
        backgroundColor: theme.colors.actionBgError,
        borderColor: theme.colors.actionBorderError,
        '&:hover:enabled': {
          backgroundColor: theme.colors.actionBgErrorHover,
          borderColor: theme.colors.actionBorderErrorHover
        },
        '&:active:enabled': {
          backgroundColor: theme.colors.actionBgErrorActive,
          borderColor: theme.colors.actionBorderErrorActive
        },
        '&:checked': {
          backgroundColor: theme.colors.actionBgError,
          borderColor: theme.colors.actionBorderError
        },
        '&:checked::before': {
          boxShadow: `inset 1em 1em ${theme.colors.actionFgError}`
        },
        '&:checked:hover:enabled': {
          backgroundColor: theme.colors.actionBgErrorHover,
          borderColor: theme.colors.actionBorderErrorHover
        },
        '&:checked:hover:enabled::before': {
          boxShadow: `inset 1em 1em ${theme.colors.actionFgErrorHover}`
        },
        '&:checked:active:enabled': {
          backgroundColor: theme.colors.actionBgErrorActive,
          borderColor: theme.colors.actionBorderErrorActive
        },
        '&:checked:active:enabled::before': {
          boxShadow: `inset 1em 1em ${theme.colors.actionFgErrorActive}`
        }
      }
    }
  }
});

/**
 * TestIDs
 */
const RadioTestIDs = {
  Radio: 'kui-radio'
};
/**
 * Defaults
 */
const RadioDefaults = {
  css: {},
  status: 'valid'
};

export { RadioDefaults, RadioStyle, RadioTestIDs };
